// import { useAuth0 } from '@auth0/auth0-react';
import { Button, Input, Label } from '@playcastdotio/Shadcn';
import { useEffect, useState, useRef, useCallback } from 'react';
import { Form, Link, useNavigate } from 'react-router-dom';
import { authStore, joinState, networkMetricsState } from '../../../api/stores/globalStores';
import { playerNameState, ruleState, invitationState, deckState, lobbyQueryState, lobbyState } from '../../../api/stores/lobbyStores';
import { faker } from '@faker-js/faker';
import { Invitation, websocket, LobbyMessageType, NotificationMessageType } from '@playcastdotio/Messaging';
import { sendMessage } from '../../../api/modules/realtimeMessaging';
import { WhyPlaycast } from '../../components/WhyPlaycast';
import { getPersistentPlayerId } from '../../../api';
import {
  sendAnalytics,
  PlaycastAnalyticsMachineRequested,
  PlaycastAnalyticsMachineRequestRejected,
  PlaycastAnalyticsMachineAssigned,
  PlaycastAnalyticsUserExited,
  PlaycastAnalyticsEmailSubmitted,
  PlaycastAnalyticsEmailSent,
} from '@playcastdotio/Analytics';

// Set to false to disable auto-connect behavior for single invitation routes
const ENABLE_AUTO_CONNECT = true;
// Set to false to disable auto-join for single invitation on non-brainrot routes
const ENABLE_SINGLE_INVITATION_AUTO_JOIN = true;

// Aliases that should use the brainrot-specific UI (promo, email capture)
const BRAINROT_ALIASES: string[] = ['bowba', 'brainrot'];

// Aliases that should auto-connect to any free lobby (superset can include brainrot aliases)
const AUTO_CONNECT_ALIASES: string[] = [...BRAINROT_ALIASES, 'bowb', 'mcdonald', 'mcdonalds', 'mcdonalds-gamescon', 'roblox', 'fortcast', 'playcastgarden2025', 'shades1', 'shades2', 'shades3', 'shades4', 'touchdemos', 'touchdemos1', 'touchdemos2','touchdemos3','touchdemos4'];

// Exported components for reuse in other pages
export const BrainrotAutoJoiningState = () => {
  const [currentMessageIndex, setCurrentMessageIndex] = useState(0);
  const messages = [
    'Spawning your session…',
    'Deploying your battle bus…',
    'Locking in your island session…',
    'Finalizing your connection…',
    'Loading Fortnite island…',
  ];

  useEffect(() => {
    const interval = setInterval(() => {
      setCurrentMessageIndex((prevIndex) => (prevIndex + 1) % messages.length);
    }, 2000);
    return () => clearInterval(interval);
  }, [messages.length]);

  return (
    <div className='flex w-full flex-col items-center'>
      <h3 className='row-auto mt-12 py-4 text-xl font-bold text-white'>Finding Available Server...</h3>
      <p className='row-auto pb-4 text-lg text-white'>{messages[currentMessageIndex]}</p>
      <div className='flex justify-center py-4'>
        <div className='mb-12 h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent'></div>
      </div>
    </div>
  );
};

export const SingleInvitationAutoJoiningState = () => {
  return (
    <div className='flex w-full flex-col items-center'>
      <h3 className='row-auto mt-12 py-4 text-xl font-bold text-white'>Connecting to session...</h3>
      <p className='row-auto pb-4 text-lg text-white'>Preparing your experience...</p>
      <div className='flex justify-center py-4'>
        <div className='mb-12 h-6 w-6 animate-spin rounded-full border-2 border-white border-t-transparent'></div>
      </div>
    </div>
  );
};

export const BrainrotNoMachinesState = () => {
  const [email, setEmail] = useState('');
  const [emailSent, setEmailSent] = useState(false);

  // Email validation regex
  const isValidEmail = (email: string): boolean => {
    const regexPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return regexPattern.test(email);
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setEmail(e.target.value);
    setEmailSent(false);
  };

  const handleSend = () => {
    if (!isValidEmail(email)) return;
    // Send NOTIFICATION_FORTNITE_REMINDER notification

    sendAnalytics({
      name: 'Email Submitted',
      role: 'host',
      persistentId: getPersistentPlayerId(),
      email: email,
    } as PlaycastAnalyticsEmailSubmitted);

    const outboundMessage = websocket.generators.notification(NotificationMessageType.NOTIFICATION_FORTNITE_REMINDER, { email });
    sendMessage(outboundMessage);
    setEmailSent(true);
    setEmail('');

    sendAnalytics({
      name: 'Email Sent',
      role: 'host',
      persistentId: getPersistentPlayerId(),
      email: email,
    } as PlaycastAnalyticsEmailSent);
  };

  return (
    <div
      className='relative mx-auto mt-8 flex max-h-screen w-full max-w-md flex-col items-center overflow-y-auto rounded-lg p-0 shadow-lg sm:max-h-[90vh]'
      style={{ background: '#26487E' }}
    >
      {/* Capacity message */}
      <div className='mb-1 mt-8 text-2xl font-bold text-white'>Join My Singing Brainrot!</div>
      {/* Promo Image */}
      <a
        href='https://www.fortnite.com/play/island/1514-8773-6943?utm_source=playcast&utm_medium=interactive&utm_campaign=brainrot-july-2025'
        target='_blank'
        rel='noopener noreferrer'
        className='block flex justify-center'
        onClick={() => {
          sendAnalytics({
            name: 'User Exited',
            role: 'host',
            persistentId: getPersistentPlayerId(),
            destination:
              'https://www.fortnite.com/play/island/1514-8773-6943?utm_source=playcast&utm_medium=interactive&utm_campaign=brainrot-july-2025',
          } as PlaycastAnalyticsUserExited);
        }}
      >
        <img
          src='/fortnitePromo.webp'
          alt='Fortnite Promo'
          className='mb-4 w-3/4 max-w-xs cursor-pointer rounded transition-opacity hover:opacity-90'
        />
      </a>
      {/* OR */}
      <div className='mb-2 text-lg font-bold text-white'>- OR -</div>
      {/* Receive a link */}
      <div className='mb-1 text-2xl font-bold text-white'>Receive a link</div>
      {/* Description */}
      <div className='mb-2 px-4 text-center text-base font-normal text-white'>
        We'll send you a link to try MY SINGING BRAINROT 🎤 (NEW) whenever you want
      </div>
      {/* Email input and send button */}
      <div className='mb-4 flex w-full flex-row items-center gap-2 px-4'>
        <input
          type='text'
          placeholder='email@domain.com'
          value={email}
          onChange={handleEmailChange}
          className='flex-1 rounded border border-gray-300 px-3 py-2 text-black focus:border-emerald-600 focus:outline-none'
        />
        <button
          type='button'
          onClick={handleSend}
          className='rounded bg-emerald-600 px-4 py-2 font-bold text-white transition hover:bg-emerald-700 active:bg-emerald-800'
          disabled={!isValidEmail(email)}
        >
          Send Link
        </button>
      </div>
      {emailSent && <div className='mb-4 text-sm text-emerald-200'>Invite sent!</div>}
    </div>
  );
};

export const AutoConnectNoMachinesState = () => {
  const getPavilionUrl = () => {
    const { protocol, hostname, port } = window.location;
    const portSuffix = port ? `:${port}` : '';

    // If already on a pavilion subdomain, keep host
    if (hostname.startsWith('pavilion.')) {
      return `${protocol}//${hostname}${portSuffix}/p/playcast`;
    }

    // Localhost handling
    if (hostname === 'localhost') {
      return `${protocol}//pavilion.localhost${portSuffix}/p/playcast`;
    }

    // playcast.io handling (preserve first env label if present)
    if (hostname.endsWith('playcast.io')) {
      const labels = hostname.split('.');
      if (labels.length === 2) {
        // playcast.io
        return `${protocol}//pavilion.playcast.io${portSuffix}/p/playcast`;
      }
      // e.g., dev.marketing.playcast.io → dev.pavilion.playcast.io
      const firstLabel = labels[0];
      return `${protocol}//${firstLabel}.pavilion.playcast.io${portSuffix}/p/playcast`;
    }

    // Fallback: prepend pavilion
    return `${protocol}//pavilion.${hostname}${portSuffix}/p/playcast`;
  };

  return (
    <div className='mt-8 flex w-full justify-center'>
      <div className='w-full space-y-4 rounded-3xl p-6 text-center shadow-md md:w-2/3 lg:w-2/5' style={{ background: '#0F1727' }}>
        <div className='flex justify-center'>
          <img src='/images/playcast_logo-96x96.png' alt='Playcast logo' className='h-12 w-12' />
        </div>
        <div className='text-base text-gray-400'>All game lobbies are full...</div>
        <div className='space-y-1 leading-tight'>
          <div className='text-lg text-white'>Join the</div>
          <div className='mb-0 font-[Syne] text-2xl font-bold text-yellow-500'>Playcast</div>
          <div className='font-[Syne] text-2xl font-bold text-yellow-500' style={{ marginTop: '-0.5rem' }}>
            Pavilion
          </div>
          <div className='text-lg text-white'>party instead!</div>
        </div>
        <div className='flex justify-center pt-2'>
          <Button
            className='bg-yellow-500 font-bold text-black hover:bg-yellow-400'
            onClick={() => (window.location.href = getPavilionUrl())}
          >
            Explore Now
          </Button>
        </div>
      </div>
    </div>
  );
};

// Utility function to detect brainrot mode from URL or joinId
export const isBrainrotMode = (joinId?: string): boolean => {
  if (joinId) {
    return BRAINROT_ALIASES.includes(joinId);
  }
  // Check URL if no joinId provided
  const pathname = window.location.pathname;
  return BRAINROT_ALIASES.some((alias) => pathname.includes(`/${alias}`));
};

// Utility function to detect if current alias should auto-connect to free lobbies (logic only, UI may differ)
export const isAutoConnectMode = (joinId?: string): boolean => {
  if (joinId) {
    return AUTO_CONNECT_ALIASES.includes(joinId);
  }
  const pathname = window.location.pathname;
  return AUTO_CONNECT_ALIASES.some((alias) => pathname.includes(`/${alias}`));
};

interface DeckProps {
  joinId?: string;
}

// Function to send LOBBY_QUERY for an invitation
const sendLobbyQuery = (invitationId: string) => {
  if (invitationId) {
    const message = websocket.generators.lobby(LobbyMessageType.LOBBY_QUERY, {
      invitationId,
    });
    sendMessage(message);
  }
};

// Function to select the invitation with the closest region based on ping
const selectClosestRegionInvitation = (invitations: Invitation[]): Invitation | null => {
  if (invitations.length === 0) {
    return null;
  }

  const pingData = networkMetricsState.currentState.ping;

  // If no ping data available, fall back to random selection
  if (!pingData || Object.keys(pingData).length === 0) {
    console.log('No ping data available, using random selection');
    const randomIndex = Math.floor(Math.random() * invitations.length);
    return invitations[randomIndex];
  }

  let bestInvitation: Invitation | null = null;
  let lowestPing = Infinity;

  console.log('Available ping data:', pingData);

  for (const invitation of invitations) {
    // Only process lobby-type invitations with regionName
    if (invitation.type === 'lobby' && invitation.manifest?.regionName) {
      const regionName = invitation.manifest.regionName;
      const regionPing = pingData[regionName];

      console.log(`Invitation "${invitation.description}" - Region: ${regionName}, Ping: ${regionPing}ms`);

      if (regionPing !== undefined && regionPing < lowestPing) {
        lowestPing = regionPing;
        bestInvitation = invitation;
      }
    }
  }

  // If no lobby invitations with valid regions found, fall back to random selection
  if (!bestInvitation) {
    console.log('No lobby invitations with valid regions found, using random selection');
    const randomIndex = Math.floor(Math.random() * invitations.length);
    return invitations[randomIndex];
  }

  console.log(`Selected closest region invitation: "${bestInvitation.description}" (${bestInvitation.manifest?.regionName}) with ping ${lowestPing}ms`);
  return bestInvitation;
};

export const Deck = ({ joinId }: DeckProps) => {
  // const { isAuthenticated, user, getAccessTokenSilently, getIdTokenClaims } = useAuth0();

  // const [selectedGameIndex, setSelectedGameIndex] = useState(0);
  const [selectedGameId, setSelectedGameId] = useState('');
  const [selectedGameInfo, setSelectedGameInfo] = useState<Invitation>();
  const [name, setName] = useState(faker.person.firstName() + ' ' + faker.person.lastName());
  const [deckGreeting, setDeckGreeting] = useState('');
  const [invitationList, setInvitationList] = useState<Invitation[]>([]);
  const [anyValid, setAnyValid] = useState(false);
  const [showBrainrotNoMachines, setShowBrainrotNoMachines] = useState(false);
  const [showBrainrotLoading, setShowBrainrotLoading] = useState(isBrainrotMode(joinId)); // Initialize based on route
  const [showAutoConnectNoMachines, setShowAutoConnectNoMachines] = useState(false);
  const [showAutoConnectLoading, setShowAutoConnectLoading] = useState(isAutoConnectMode(joinId));
  const [showSingleInvitationAutoJoin, setShowSingleInvitationAutoJoin] = useState(false);
  const [hasAttemptedAutoJoin, setHasAttemptedAutoJoin] = useState(false);
  const [joinedInvitationId, setJoinedInvitationId] = useState<string | null>(null);
  const [pendingLobbyQueries, setPendingLobbyQueries] = useState<Set<string>>(new Set());
  const pendingLobbyQueriesRef = useRef<Set<string>>(new Set());
  // const navigate = useNavigate();

  // Check if we're on the special fn_brainrot route
  const isBrainrotRoute = isBrainrotMode(joinId);
  const isAutoConnectRoute = isAutoConnectMode(joinId);

  useEffect(() => {
    // if (!isAuthenticated && ruleState.currentState.requireAuthOnDeck) {
    //   navigate('/sts');
    // }

    setName(faker.person.firstName() + ' ' + faker.person.lastName());
    // Initialize ref with current state
    pendingLobbyQueriesRef.current = pendingLobbyQueries;

    // focus the first button also
    // const buttonGrid = document.getElementById('buttonGrid');
    // if (buttonGrid) {
    //   const buttons = buttonGrid.getElementsByTagName('button');
    //   buttons[0].click();
    // }
  }, [isBrainrotRoute]);

  // Helper function to check if an invitation has no players
  const isInvitationEmpty = (invitation: Invitation): boolean => {
    if (invitation.type === 'cafe') {
      // For cafe type, ignore.
      return false;
    }

    if (invitation.type === 'lobby') {
      // For lobby type, check the lobby query state
      const lobbyData = lobbyQueryState.currentState[invitation.id];
      if (lobbyData) {
        // If we have lobby data, check if there are no players. Also check that we arent in the lobby.
        const isInLobby = lobbyData.players.some((player) => player.playerId === lobbyState.currentState.lobbyPlayerId);
        const isValid = lobbyData.connectedPlayerCount !== -1;
        if (!isValid) {
          return false;
        }
        const totalPlayerCount = lobbyData.connectedPlayerCount + lobbyData.players.length;
        const isEmpty = totalPlayerCount === 0 || isInLobby;

        //         alert(`DEBUG: Lobby invitation "${invitation.description}" (${invitation.id}):
        // - connectedPlayerCount: ${lobbyData.connectedPlayerCount}
        // - players.length: ${lobbyData.players.length}
        // - totalPlayerCount: ${totalPlayerCount}
        // - isInLobby: ${isInLobby}
        // - isEmpty: ${isEmpty}`);

        return isEmpty;
      }
      // If we don't have lobby data yet, assume it's not empty (we'll query it)
      // alert(`DEBUG: Lobby invitation "${invitation.description}" - no lobby data yet, assuming not empty`);
      return false;
    }

    // alert(`DEBUG: Unknown invitation type: ${invitation.type }`);
    return false;
  };

  // Function to run auto-connect logic (brainrot and other aliases) when all lobby queries are complete
  const runBrainrotLogic = () => {
    if (!ENABLE_AUTO_CONNECT || !isAutoConnectRoute) {
      return;
    }

    // Check if initial invitation loading is complete (onAlias has been called)
    const hasReceivedInitialInvitations = deckState.currentState.invitations !== undefined;

    if (!hasReceivedInitialInvitations) {
      console.log('Still waiting for initial invitation response');
      // Show loading while waiting for initial data
      const setLoading = isBrainrotRoute ? setShowBrainrotLoading : setShowAutoConnectLoading;
      const setNoMachines = isBrainrotRoute ? setShowBrainrotNoMachines : setShowAutoConnectNoMachines;
      setLoading(true);
      setNoMachines(false);
      return;
    }

    // Get all valid invitations from deckState directly
    const allValidInvitations = (deckState.currentState.invitations || []).filter((invite) => !invite.invalid);

    // ALWAYS show loading if we have pending lobby queries - don't show capacity until ALL are resolved
    if (pendingLobbyQueries.size > 0) {
      console.log('Still waiting for lobby queries:', Array.from(pendingLobbyQueries));
      // Keep showing loading while waiting for lobby queries
      const setLoading = isBrainrotRoute ? setShowBrainrotLoading : setShowAutoConnectLoading;
      const setNoMachines = isBrainrotRoute ? setShowBrainrotNoMachines : setShowAutoConnectNoMachines;
      setLoading(true);
      setNoMachines(false);
      return;
    }

    console.log('All lobby queries complete, checking for empty invitations');

    // Now we can safely check - all queries have been sent and resolved
    if (allValidInvitations.length === 0) {
      console.log('No valid invitations exist at all for auto-connect');
      const setLoading = isBrainrotRoute ? setShowBrainrotLoading : setShowAutoConnectLoading;
      const setNoMachines = isBrainrotRoute ? setShowBrainrotNoMachines : setShowAutoConnectNoMachines;
      setLoading(false);
      // For brainrot, show capacity UI; for other auto-connect aliases, fall back to "not active"
      setNoMachines(isBrainrotRoute);
      return;
    }

    // Check which valid invitations are actually empty
    const emptyInvitations = allValidInvitations.filter(isInvitationEmpty);

    sendAnalytics({
      name: 'Machine Requested',
      role: 'host',
      availableMachinesCount: emptyInvitations.length,
      persistentId: getPersistentPlayerId(),
    } as PlaycastAnalyticsMachineRequested);

    if (emptyInvitations.length > 0) {
      // Pick the empty invitation with the closest region based on ping
      const selectedInvitation = selectClosestRegionInvitation(emptyInvitations);

      if (selectedInvitation) {
        sendAnalytics({
          name: 'Machine Assigned',
          role: 'host',
          persistentId: getPersistentPlayerId(),
        } as PlaycastAnalyticsMachineAssigned);

        console.log('Auto-joining closest region invitation for auto-connect route:', selectedInvitation.description, 'Region:', selectedInvitation.manifest?.regionName);
        const setLoading = isBrainrotRoute ? setShowBrainrotLoading : setShowAutoConnectLoading;
        const setNoMachines = isBrainrotRoute ? setShowBrainrotNoMachines : setShowAutoConnectNoMachines;
        setLoading(false);
        setNoMachines(false);
        handleJoinQueue(selectedInvitation.id);
      } else {
        // Fallback if selection failed - show no machines state
        console.log('Failed to select invitation, showing no machines state');
        const setLoading = isBrainrotRoute ? setShowBrainrotLoading : setShowAutoConnectLoading;
        const setNoMachines = isBrainrotRoute ? setShowBrainrotNoMachines : setShowAutoConnectNoMachines;
        setLoading(false);
        setNoMachines(true);
      }
    } else {
      // Valid invitations exist but none are empty - at capacity (ONLY after all queries resolved)
      console.log('Valid invitations exist but none are empty - at capacity');

      sendAnalytics({
        name: 'Machine Request Rejected',
        role: 'host',
        persistentId: getPersistentPlayerId(),
      } as PlaycastAnalyticsMachineRequestRejected);

      const setLoading = isBrainrotRoute ? setShowBrainrotLoading : setShowAutoConnectLoading;
      const setNoMachines = isBrainrotRoute ? setShowBrainrotNoMachines : setShowAutoConnectNoMachines;
      setLoading(false);
      setNoMachines(true);
    }
  };

  // Function to run single invitation auto-join logic for non-auto-connect routes
  const runSingleInvitationAutoJoin = () => {
    if (!ENABLE_SINGLE_INVITATION_AUTO_JOIN || isAutoConnectRoute) {
      return;
    }

    // Prevent multiple auto-join attempts
    if (hasAttemptedAutoJoin) {
      console.log('Auto-join already attempted, skipping');
      return;
    }

    console.log('Running single invitation auto-join logic');

    // Check if initial invitation loading is complete (onAlias has been called)
    const hasReceivedInitialInvitations = deckState.currentState.invitations !== undefined;

    if (!hasReceivedInitialInvitations) {
      console.log('Still waiting for initial invitation response for single invitation auto-join');
      return;
    }

    // Get all valid invitations from deckState (not filtered for empty like brainrot logic)
    const allValidInvitations = (deckState.currentState.invitations || []).filter((invite) => !invite.invalid);

    // If there's exactly one valid invitation, auto-join it
    if (allValidInvitations.length === 1) {
      const singleInvitation = allValidInvitations[0];
      console.log('Auto-joining single invitation for non-brainrot route:', singleInvitation.description);
      setShowSingleInvitationAutoJoin(true);
      setHasAttemptedAutoJoin(true); // Mark that we've attempted auto-join

      // Auto-join after a brief delay to show the loading state
      setTimeout(() => {
        handleJoinQueue(singleInvitation.id);
      }, 500);
    } else {
      // Multiple invitations or no valid invitations - show normal interface
      setShowSingleInvitationAutoJoin(false);
    }
  };

  const handleJoinQueue = (selectedId: string) => {
    // event.preventDefault();
    console.log('Continue');

    // Prevent joining the same invitation multiple times
    if (joinedInvitationId === selectedId) {
      console.log('Already joined invitation with ID:', selectedId, 'skipping');
      return;
    }

    // save name

    // First try to find in the filtered invitationList (for brainrot logic)
    let selectedGameInfoLocal = invitationList.find((invite) => invite.id === selectedId);

    // If not found, try to find in all valid invitations from deckState (for single invitation auto-join)
    if (!selectedGameInfoLocal) {
      const allValidInvitations = (deckState.currentState.invitations || []).filter((invite) => !invite.invalid);
      selectedGameInfoLocal = allValidInvitations.find((invite) => invite.id === selectedId);
    }

    // Mark this invitation as joined to prevent duplicate joins
    setJoinedInvitationId(selectedId);

    playerNameState.updateState({ name: name });
    if (name) {
      // saveName();
    }
    invitationState.updateState({
      ...invitationState.currentState,
      selectedInvite: selectedGameInfoLocal,
    });
    joinState.updateState({
      ...joinState.currentState,
      state: 'inQueue',
    });
  };

  useEffect(() => {
    const updateSelectedGameInfo = () => {
      const invites = Object.values(invitationState.currentState.invites).filter((invite) => invite !== false);
      if (invites.length > 0) {
        const info = invites.find((invite) => invite.id === selectedGameId);
        if (info) {
          setSelectedGameInfo(info);
        }
      }
    };

    updateSelectedGameInfo();

    // Set up listener specifically for selection changes
    const listener = invitationState.addListener(() => {
      updateSelectedGameInfo();
    });

    return () => {
      invitationState.removeListener(listener);
    };
  }, [selectedGameId]);

  useEffect(() => {
    const deck = deckState.currentState;
    if (deck) {
      setDeckGreeting(deck.greeting);
    }

    const updateInvitationData = (deckStateData = deckState.currentState) => {
      const invites = (deckStateData.invitations || []).filter((invite) => !invite.invalid);

      // sort alphabetically
      const alphabeticalInvites = invites.sort((a, b) => {
        const adesc = a.description || '';
        const bdesc = b.description || '';
        return adesc.localeCompare(bdesc);
      });

      setInvitationList(alphabeticalInvites);
      setAnyValid(invites.length > 0);
      console.log('Invitation list updated:', alphabeticalInvites);

      // Track lobby-type invitations and send queries
      const lobbyInvitations = alphabeticalInvites.filter((inv) => inv.type === 'lobby');
      const newPendingQueries = new Set(lobbyInvitations.map((inv) => inv.id));

      // Only update if the Set contents actually changed
      const currentPendingArray = Array.from(pendingLobbyQueries).sort();
      const newPendingArray = Array.from(newPendingQueries).sort();
      const hasChanged =
        currentPendingArray.length !== newPendingArray.length || currentPendingArray.some((id, index) => id !== newPendingArray[index]);

      if (hasChanged) {
        setPendingLobbyQueries(newPendingQueries);
        pendingLobbyQueriesRef.current = newPendingQueries;
        console.log('Sending lobby queries for:', Array.from(newPendingQueries));

        // Send LOBBY_QUERY for each lobby-type invitation
        lobbyInvitations.forEach((invitation) => {
          sendLobbyQuery(invitation.id);

          // Add timeout to remove pending queries that don't resolve within 2 seconds
          setTimeout(() => {
            setPendingLobbyQueries((prev) => {
              const newSet = new Set(prev);
              if (newSet.has(invitation.id)) {
                console.log('Timeout: Removing pending lobby query for invitation:', invitation.id);
                newSet.delete(invitation.id);
                pendingLobbyQueriesRef.current = newSet;

                // If all queries are now complete (either resolved or timed out), trigger brainrot logic
                if (newSet.size === 0) {
                  console.log('All lobby queries completed or timed out, triggering brainrot logic');
                  setTimeout(runBrainrotLogic, 0);
                }
              }
              return newSet;
            });
          }, 2000); // 2 second timeout
        });
      }

      // If no lobby queries needed, run brainrot logic immediately
      if (newPendingQueries.size === 0) {
        setTimeout(runBrainrotLogic, 0); // Use setTimeout to avoid running during render
      }
    };

    // Initial load
    updateInvitationData();

    // Set up listener for state changes
    const listener = deckState.addListener((state) => {
      updateInvitationData(state);
    });

    // Set up listener for lobby query state changes
    const lobbyQueryListener = lobbyQueryState.addListener((state) => {
      // Check if any of our pending queries completed
      const completedQueries = Array.from(pendingLobbyQueriesRef.current).filter((invitationId) => state[invitationId] !== undefined);

      if (completedQueries.length > 0) {
        console.log('Lobby queries completed:', completedQueries);

        // Remove completed queries from pending set
        setPendingLobbyQueries((prev) => {
          const newSet = new Set(prev);
          completedQueries.forEach((id) => newSet.delete(id));
          pendingLobbyQueriesRef.current = newSet;
          return newSet;
        });

        // Check if all queries are now complete and run brainrot logic
        const remainingQueries = pendingLobbyQueriesRef.current.size;
        if (remainingQueries === 0) {
          console.log('All lobby queries completed, triggering brainrot logic');
          setTimeout(runBrainrotLogic, 0); // Use setTimeout to avoid running during render
        }
      }
    });

    return () => {
      deckState.removeListener(listener);
      lobbyQueryState.removeListener(lobbyQueryListener);
    };
  }, [isAutoConnectRoute]);

  // Effect to run auto-connect logic when conditions change and all queries are complete
  useEffect(() => {
    if (isAutoConnectRoute && pendingLobbyQueries.size === 0) {
      console.log('Dependencies changed and all queries complete, running brainrot logic');
      runBrainrotLogic();
    }
  }, [isAutoConnectRoute, pendingLobbyQueries, lobbyQueryState.currentState, deckState.currentState.invitations]);

  // Effect to run single invitation auto-join logic when conditions change and all queries are complete
  useEffect(() => {
    if (!isAutoConnectRoute) {
      console.log('Dependencies changed, running single invitation auto-join logic');
      runSingleInvitationAutoJoin();
    }
  }, [isAutoConnectRoute, deckState.currentState.invitations]);

  const handleNameChanged = (event: React.ChangeEvent<HTMLInputElement>) => {
    // setName(event.target.value);
    playerNameState.updateState({ name: event.target.value });
  };

  return (
    <section className='mt-8 flex flex-col items-center justify-center p-2 text-center text-white md:p-8'>
      <div
        className='font-[Syne] text-3xl font-medium [&>a]:text-yellow-400 [&>a]:hover:underline'
        dangerouslySetInnerHTML={{ __html: deckGreeting || '' }}
      />
      {isBrainrotRoute ? (
        showBrainrotLoading ? (
          <BrainrotAutoJoiningState />
        ) : showBrainrotNoMachines ? (
          <BrainrotNoMachinesState />
        ) : ENABLE_AUTO_CONNECT ? (
          <BrainrotAutoJoiningState />
        ) : anyValid ? (
          <Form className='flex flex-col items-center'>
            <div className='row-auto mt-4 w-full lg:w-1/3'>
              <Input
                value={name}
                onChange={handleNameChanged}
                type='text'
                id='name'
                name='name'
                required
                placeholder='Name'
                className='bg-content mb-2 h-auto w-full rounded border-none px-4 py-3 text-center text-lg shadow-sm'
              />
            </div>

            <span className='py-8 text-gray-400'>Choose a Playcast to join</span>

            <div className='grid grid-cols-1 gap-y-4 md:grid-cols-2 lg:grid-cols-3' id='buttonGrid'>
              {invitationList.map((invitation, index) => {
                return (
                  <button key={index} className='relative mx-2 lg:mx-4' onClick={() => handleJoinQueue(invitation.id)}>
                    {/* <img src={`/images/shields/shield.png`} alt={`shield-${index}`} className='size-20 md:size-28 lg:size-48' /> */}
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      baseProfile='basic'
                      viewBox='0 0 64 64'
                      className='svg-shadow size-20 md:size-28 lg:size-48'
                    >
                      <defs>
                        <filter id='default-shadow' x='-50%' y='-50%' width='200%' height='200%'>
                          <feDropShadow dx='4' dy='4' stdDeviation='4' floodColor='#000' floodOpacity='0.5' />
                        </filter>

                        <filter id='hover-shadow' x='-50%' y='-50%' width='200%' height='200%'>
                          <feDropShadow dx='4' dy='4' stdDeviation='4' floodColor='rgba(250, 204, 21, 255)' floodOpacity='0.1' />
                        </filter>
                      </defs>
                      <path
                        fill='#a7b3c7'
                        d='M28.69,7.589l-17.028,8.173c-2.917,1.4-4.647,4.48-4.298,7.697	c1.038,9.56,5.037,20.261,20.435,30.215c2.559,1.654,5.843,1.654,8.401,0C51.599,43.72,55.597,33.02,56.636,23.459	c0.349-3.217-1.381-6.297-4.298-7.697L35.31,7.589C33.218,6.584,30.782,6.584,28.69,7.589z'
                        filter='url(#default-shadow)'
                      />
                      <path
                        fill='#ffa602'
                        d='M32,49.916c-0.527,0-1.041-0.152-1.486-0.439C19.831,42.57,13.715,35.625,12.335,22.92	c-0.12-1.1,0.479-2.165,1.491-2.65l17.028-8.173c0.361-0.173,0.747-0.261,1.146-0.261s0.786,0.088,1.146,0.261l17.028,8.174	c1.011,0.485,1.61,1.55,1.491,2.649c-1.38,12.706-7.496,19.65-18.179,26.556C33.041,49.764,32.527,49.916,32,49.916z'
                        filter='url(#default-shadow)'
                      />
                      <path
                        d='M33.486,49.476c10.683-6.906,16.799-13.85,18.179-26.556c0.12-1.1-0.479-2.164-1.491-2.649	l-17.028-8.174c-0.36-0.173-0.746-0.261-1.146-0.261v38.08C32.527,49.916,33.041,49.764,33.486,49.476z'
                        opacity='.2'
                        filter='url(#default-shadow)'
                      />
                      <line
                        x1='17.434'
                        x2='26.609'
                        y1='18.538'
                        y2='14.134'
                        fill='none'
                        stroke='#fff'
                        strokeLinecap='round'
                        strokeMiterlimit='10'
                        strokeWidth='3'
                      />
                      <path
                        fill='#fff'
                        d='M33.636,7.015c-1.651-0.362-3.393-0.172-4.946,0.573l-17.028,8.173	c-2.917,1.4-4.647,4.48-4.298,7.697c0.168,1.543,0.414,3.196,0.772,4.922c2.621-0.411,4.489-2.797,4.199-5.462	c-0.12-1.1,0.479-2.164,1.491-2.649l17.028-8.173C32.828,11.149,33.874,9.075,33.636,7.015z'
                        opacity='.3'
                        filter='url(#default-shadow)'
                      />
                      <path
                        d='M56.636,23.459c0.349-3.217-1.381-6.297-4.298-7.697l-4.508-2.164c0,0,0,0,0,0	c-1.195,2.489-0.146,5.476,2.344,6.671c1.011,0.485,1.61,1.55,1.491,2.65c-1.38,12.706-7.496,19.65-18.179,26.556	c-1.84,1.19-2.617,3.381-2.119,5.395c1.668,0.137,3.369-0.249,4.834-1.196C51.599,43.72,55.597,33.02,56.636,23.459z'
                        opacity='.15'
                        filter='url(#default-shadow)'
                      />
                      <ellipse cx='32' cy='60' opacity='.3' rx='13' ry='3' />
                    </svg>
                    <span
                      className='text-md absolute bottom-0 left-1/2 w-full -translate-x-1/2 transform font-bold text-white md:text-xl'
                      style={{
                        textShadow: '2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000',
                      }}
                    >
                      {invitation.description || ''}
                    </span>
                    <span
                      className='md:text-md absolute -bottom-5 left-1/2 w-full -translate-x-1/2 transform text-sm font-bold text-white'
                      style={{
                        textShadow: '2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000',
                      }}
                    >
                      {''}
                    </span>
                    {/* {selectedGameIndex === index && (
                  <span
                    className='text-primary text-xl font-bold absolute right-0 w-fit top-0 rotate-[8deg] lg:right-5 lg:top-5 lg:text-2xl'
                    style={{
                      textShadow: '2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000',
                    }}
                  >
                    Yeah!
                  </span>
                )} */}
                  </button>
                );
              })}
            </div>

            {/* <h1 className='mt-8 font-[Syne] text-2xl font-medium'>{selectedGameInfo?.description}</h1> */}

            {/* <div className='mt-14 py-2 text-sm'>
            <div>
              There are {selectedGameInfo?.pendingCount === -1 ? '' : selectedGameInfo?.pendingCount} people currently in this queue
            </div>
          </div> */}
          </Form>
        ) : (
          <div className='mt-8 flex w-full justify-center'>
            <div className='w-full space-y-4 rounded-lg bg-gray-700 p-4 py-6 text-center shadow-md md:w-2/3 lg:w-2/5'>
              <h3 className='row-auto text-xl font-bold'>This page is not currently active</h3>
              <p className='row-auto'>
                The playcaster may not have started any broadcasts yet. Please check back later or contact the playcaster for more
                information.
              </p>
            </div>
          </div>
        )
      ) : isAutoConnectRoute ? (
        showAutoConnectLoading ? (
          <SingleInvitationAutoJoiningState />
        ) : showAutoConnectNoMachines ? (
          <AutoConnectNoMachinesState />
        ) : ENABLE_AUTO_CONNECT && anyValid ? (
          <SingleInvitationAutoJoiningState />
        ) : (
          <AutoConnectNoMachinesState />
        )
      ) : anyValid ? (
        showSingleInvitationAutoJoin ? (
          <SingleInvitationAutoJoiningState />
        ) : (
          <Form className='flex flex-col items-center'>
            <div className='row-auto mt-4 w-full lg:w-1/3'>
              <Input
                value={name}
                onChange={handleNameChanged}
                type='text'
                id='name'
                name='name'
                required
                placeholder='Name'
                className='bg-content mb-2 h-auto w-full rounded border-none px-4 py-3 text-center text-lg shadow-sm'
              />
            </div>

            <span className='py-8 text-gray-400'>Choose a Playcast to join</span>

            <div className='grid grid-cols-1 gap-y-4 md:grid-cols-2 lg:grid-cols-3' id='buttonGrid'>
              {invitationList.map((invitation, index) => {
                return (
                  <button key={index} className='relative mx-2 lg:mx-4' onClick={() => handleJoinQueue(invitation.id)}>
                    {/* <img src={`/images/shields/shield.png`} alt={`shield-${index}`} className='size-20 md:size-28 lg:size-48' /> */}
                    <svg
                      xmlns='http://www.w3.org/2000/svg'
                      baseProfile='basic'
                      viewBox='0 0 64 64'
                      className='svg-shadow size-20 md:size-28 lg:size-48'
                    >
                      <defs>
                        <filter id='default-shadow' x='-50%' y='-50%' width='200%' height='200%'>
                          <feDropShadow dx='4' dy='4' stdDeviation='4' floodColor='#000' floodOpacity='0.5' />
                        </filter>

                        <filter id='hover-shadow' x='-50%' y='-50%' width='200%' height='200%'>
                          <feDropShadow dx='4' dy='4' stdDeviation='4' floodColor='rgba(250, 204, 21, 255)' floodOpacity='0.1' />
                        </filter>
                      </defs>
                      <path
                        fill='#a7b3c7'
                        d='M28.69,7.589l-17.028,8.173c-2.917,1.4-4.647,4.48-4.298,7.697	c1.038,9.56,5.037,20.261,20.435,30.215c2.559,1.654,5.843,1.654,8.401,0C51.599,43.72,55.597,33.02,56.636,23.459	c0.349-3.217-1.381-6.297-4.298-7.697L35.31,7.589C33.218,6.584,30.782,6.584,28.69,7.589z'
                        filter='url(#default-shadow)'
                      />
                      <path
                        fill='#ffa602'
                        d='M32,49.916c-0.527,0-1.041-0.152-1.486-0.439C19.831,42.57,13.715,35.625,12.335,22.92	c-0.12-1.1,0.479-2.165,1.491-2.65l17.028-8.173c0.361-0.173,0.747-0.261,1.146-0.261s0.786,0.088,1.146,0.261l17.028,8.174	c1.011,0.485,1.61,1.55,1.491,2.649c-1.38,12.706-7.496,19.65-18.179,26.556C33.041,49.764,32.527,49.916,32,49.916z'
                        filter='url(#default-shadow)'
                      />
                      <path
                        d='M33.486,49.476c10.683-6.906,16.799-13.85,18.179-26.556c0.12-1.1-0.479-2.164-1.491-2.649	l-17.028-8.174c-0.36-0.173-0.746-0.261-1.146-0.261v38.08C32.527,49.916,33.041,49.764,33.486,49.476z'
                        opacity='.2'
                        filter='url(#default-shadow)'
                      />
                      <line
                        x1='17.434'
                        x2='26.609'
                        y1='18.538'
                        y2='14.134'
                        fill='none'
                        stroke='#fff'
                        strokeLinecap='round'
                        strokeMiterlimit='10'
                        strokeWidth='3'
                      />
                      <path
                        fill='#fff'
                        d='M33.636,7.015c-1.651-0.362-3.393-0.172-4.946,0.573l-17.028,8.173	c-2.917,1.4-4.647,4.48-4.298,7.697c0.168,1.543,0.414,3.196,0.772,4.922c2.621-0.411,4.489-2.797,4.199-5.462	c-0.12-1.1,0.479-2.164,1.491-2.649l17.028-8.173C32.828,11.149,33.874,9.075,33.636,7.015z'
                        opacity='.3'
                        filter='url(#default-shadow)'
                      />
                      <path
                        d='M56.636,23.459c0.349-3.217-1.381-6.297-4.298-7.697l-4.508-2.164c0,0,0,0,0,0	c-1.195,2.489-0.146,5.476,2.344,6.671c1.011,0.485,1.61,1.55,1.491,2.65c-1.38,12.706-7.496,19.65-18.179,26.556	c-1.84,1.19-2.617,3.381-2.119,5.395c1.668,0.137,3.369-0.249,4.834-1.196C51.599,43.72,55.597,33.02,56.636,23.459z'
                        opacity='.15'
                        filter='url(#default-shadow)'
                      />
                      <ellipse cx='32' cy='60' opacity='.3' rx='13' ry='3' />
                    </svg>
                    <span
                      className='text-md absolute bottom-0 left-1/2 w-full -translate-x-1/2 transform font-bold text-white md:text-xl'
                      style={{
                        textShadow: '2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000',
                      }}
                    >
                      {invitation.description || ''}
                    </span>
                    <span
                      className='md:text-md absolute -bottom-5 left-1/2 w-full -translate-x-1/2 transform text-sm font-bold text-white'
                      style={{
                        textShadow: '2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000',
                      }}
                    >
                      {''}
                    </span>
                    {/* {selectedGameIndex === index && (
                  <span
                    className='text-primary text-xl font-bold absolute right-0 w-fit top-0 rotate-[8deg] lg:right-5 lg:top-5 lg:text-2xl'
                    style={{
                      textShadow: '2px 2px 0 #000, -2px 2px 0 #000, 2px -2px 0 #000, -2px -2px 0 #000',
                    }}
                  >
                    Yeah!
                  </span>
                )} */}
                  </button>
                );
              })}
            </div>

            {/* <h1 className='mt-8 font-[Syne] text-2xl font-medium'>{selectedGameInfo?.description}</h1> */}

            {/* <div className='mt-14 py-2 text-sm'>
            <div>
              There are {selectedGameInfo?.pendingCount === -1 ? '' : selectedGameInfo?.pendingCount} people currently in this queue
            </div>
          </div> */}
          </Form>
        )
      ) : (
        <div className='mt-8 flex w-full justify-center'>
          <div className='w-full space-y-4 rounded-lg bg-gray-700 p-4 py-6 text-center shadow-md md:w-2/3 lg:w-2/5'>
            <h3 className='row-auto text-xl font-bold'>This page is not currently active</h3>
            <p className='row-auto'>
              The playcaster may not have started any broadcasts yet. Please check back later or contact the playcaster for more
              information.
            </p>
          </div>
        </div>
      )}
    </section>
  );
};
